import React, { useState, useEffect } from "react";
import {
  TextField, Button, IconButton, InputAdornment, Typography, Box,
  Paper, FormControlLabel, Checkbox, Collapse, Alert, Fade,
  Divider, CircularProgress, Chip,
} from "@mui/material";
import {
  Visibility, VisibilityOff, Key, VerifiedUser, Send,
  ArrowBack, LockReset, Security, WhatsApp, PictureAsPdf,
  NotificationsActive, Schedule, BarChart, ZoomIn,
  Check, Shield, Folder,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

const RadarAnimation = () => (
  <Box sx={{ position: "relative", width: 200, height: 200, flexShrink: 0 }}>
    <style>{`
      @keyframes radar-sweep { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      @keyframes radar-blip  { 0%,100%{opacity:.2} 50%{opacity:.9} }
      .radar-sweep-g { transform-origin:100px 100px; animation:radar-sweep 3.5s linear infinite; }
      .rb1 { animation:radar-blip 3.5s ease-in-out infinite 0s; }
      .rb2 { animation:radar-blip 3.5s ease-in-out infinite 1.3s; }
      .rb3 { animation:radar-blip 3.5s ease-in-out infinite 2.5s; }
      .rb4 { animation:radar-blip 3.5s ease-in-out infinite 0.7s; }
    `}</style>
    <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="sweepGrad" cx="100" cy="100" r="92" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#667eea" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#667eea" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="100" cy="100" r="92" stroke="rgba(102,126,234,0.16)" strokeWidth="0.8" />
      <circle cx="100" cy="100" r="67" stroke="rgba(102,126,234,0.20)" strokeWidth="0.8" />
      <circle cx="100" cy="100" r="43" stroke="rgba(102,126,234,0.26)" strokeWidth="0.8" />
      <circle cx="100" cy="100" r="20" stroke="rgba(102,126,234,0.33)" strokeWidth="0.8" />
      <line x1="100" y1="8" x2="100" y2="192" stroke="rgba(102,126,234,0.11)" strokeWidth="0.6" />
      <line x1="8" y1="100" x2="192" y2="100" stroke="rgba(102,126,234,0.11)" strokeWidth="0.6" />
      <g className="radar-sweep-g">
        <path d="M100 100 L100 8 A92 92 0 0 1 178 148 Z" fill="url(#sweepGrad)" opacity="0.4" />
        <line x1="100" y1="100" x2="100" y2="8" stroke="rgba(102,126,234,0.6)" strokeWidth="1" />
      </g>
      <circle className="rb1" cx="143" cy="55" r="4" fill="#667eea" />
      <circle className="rb2" cx="57" cy="132" r="3.5" fill="#667eea" />
      <circle className="rb3" cx="154" cy="125" r="3" fill="#667eea" />
      <circle className="rb4" cx="78" cy="50" r="2.5" fill="#667eea" />
      <circle cx="100" cy="100" r="4" fill="#667eea" opacity="0.85" />
    </svg>
  </Box>
);

const BrandHeader = () => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: 1.5,
    width: "100%", maxWidth: 360,
  }}>
    <Box sx={{
      width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
      background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 14px rgba(102,126,234,0.35)",
    }}>
      <img src="/Imagen2.png" alt="HSE.IA" style={{ width: 22, height: 22 }} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1.15 }}>
        HSE.IA
      </Typography>
      <Typography sx={{ fontSize: 12, color: "rgba(180,185,220,.7)" }}>
        {t("login.brandSubtitle")}
      </Typography>
    </Box>
  </Box>
);

const WhatsAppBotCard = () => (
  <Box sx={{
    width: "100%",
    maxWidth: 360,
    border: "1px solid rgba(102,126,234,0.2)",
    borderRadius: 3,
    p: "22px 24px",
    background: "rgba(255,255,255,0.03)",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: "10px", flexShrink: 0,
        background: "rgba(102,126,234,0.13)",
        border: "1px solid rgba(102,126,234,0.22)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <WhatsApp sx={{ fontSize: 19, color: "#667eea" }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#e0e4ff", lineHeight: 1.2 }}>
          {t("login.whatsappBotTitle")}
        </Typography>
      </Box>
    </Box>

    <Typography sx={{ fontSize: 13, color: "rgba(185,190,228,.72)", lineHeight: 1.65, mb: 2 }}>
      {t("login.whatsappBotDescriptionPre")}{" "}
      <Box component="span" sx={{ fontWeight: 700, color: "#e0e4ff" }}>
        {t("login.whatsappBotDescriptionBold")}
      </Box>
      {t("login.whatsappBotDescriptionPost")}
    </Typography>

    {[
      t("login.whatsappBotFeature1"),
      t("login.whatsappBotFeature2"),
    ].map((text, i) => (
      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: i === 0 ? 1 : 1.5 }}>
        <Check sx={{ fontSize: 15, color: "#667eea", flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: "rgba(175,180,218,.65)" }}>{text}</Typography>
      </Box>
    ))}

    <Divider sx={{ borderColor: "rgba(102,126,234,0.12)", mb: 1.5 }} />

    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Shield sx={{ fontSize: 14, color: "rgba(102,126,234,.7)", flexShrink: 0, mt: "1px" }} />
      <Typography sx={{ fontSize: 12, color: "rgba(160,165,205,.6)", lineHeight: 1.5 }}>
        {t("login.whatsappBotSecurityNote")}
      </Typography>
    </Box>
  </Box>
);

const DataDrillingCard = () => (
  <Box sx={{
    width: "100%",
    maxWidth: 360,
    border: "1px solid rgba(93,202,165,0.2)",
    borderRadius: 3,
    p: "22px 24px",
    background: "rgba(255,255,255,0.02)",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: "10px", flexShrink: 0,
        background: "rgba(29,158,117,0.12)",
        border: "1px solid rgba(29,158,117,0.22)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Folder sx={{ fontSize: 19, color: "#5dcaa5" }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#cef0e4", lineHeight: 1.2 }}>
          {t("login.dataDrillingTitle")}
        </Typography>
      </Box>
    </Box>

    <Typography sx={{ fontSize: 13, color: "rgba(180,235,215,.65)", lineHeight: 1.65, mb: 2 }}>
      {t("login.dataDrillingDescriptionPre")}{" "}
      <Box component="span" sx={{ fontWeight: 700, color: "#cef0e4" }}>
        {t("login.dataDrillingDescriptionBold")}
      </Box>
      {t("login.dataDrillingDescriptionPost")}
    </Typography>

    {[
      t("login.dataDrillingFeature1"),
      t("login.dataDrillingFeature2"),
    ].map((text, i) => (
      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: i === 0 ? 1 : 0 }}>
        <Check sx={{ fontSize: 15, color: "#5dcaa5", flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: "rgba(175,180,218,.65)" }}>{text}</Typography>
      </Box>
    ))}
  </Box>
);

const Login = () => {
  const navigate = useNavigate();
  const { login, auth, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({ loginInput: "", password: "", rememberMe: false });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const [forgotStep, setForgotStep] = useState("hidden");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  useEffect(() => {
    if (!authLoading && auth) navigate("/panel/mapa-proyecto", { replace: true });
  }, [auth, authLoading, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    setError((p) => ({ ...p, [name]: "" }));
    setServerError("");
  };

  const parseLoginInput = () => {
    const v = formData.loginInput.trim();
    return v.includes("@") ? { email: v, userName: "" } : { email: "", userName: v };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!formData.loginInput) errs.loginInput = "usernameOrEmailRequired";
    if (!formData.password) errs.password = "passwordRequired";
    if (Object.keys(errs).length) return setError(errs);

    const { email, userName } = parseLoginInput();
    setLoading(true); setServerError("");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/login`,
        { email, userName, password: formData.password, rememberMe: formData.rememberMe },
        { withCredentials: true }
      );
      login({ userName: res.data.userName, token: res.data.token, role: res.data.role, rememberMe: formData.rememberMe });
      navigate("/panel/mapa-proyecto");
    } catch (err) {
      const be = err?.response?.data?.error;
      if (be === "emailNotVerified") setForgotStep("code");
      else setServerError(be || "serverError");
    } finally { setLoading(false); }
  };

  const resetForgotState = () => {
    setForgotStep("hidden"); setForgotEmail(""); setForgotCode("");
    setForgotError(""); setForgotSuccess("");
  };

  const handleForgotEmailSubmit = async () => {
    if (!forgotEmail) { setForgotError(t("login.emailRequired")); return; }
    setForgotLoading(true); setForgotError(""); setForgotSuccess("");
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/api/forgot-password-login`, { email: forgotEmail });
      if (data.needsVerification) { setForgotStep("code"); setForgotSuccess(t(`login.${data.error}`)); return; }
      if (data.success === false) { setForgotError(t(`login.${data.error}`) || t("login.serverError")); return; }
      if (data.success === true) { setForgotSuccess("login.recoveryEmailSent"); setForgotStep("resetLink"); }
    } catch { setForgotError(t("login.serverError")); }
    finally { setForgotLoading(false); }
  };

  const handleResendForgotCode = async () => {
    setForgotLoading(true); setForgotError(""); setForgotSuccess("");
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/forgot-password-login`, { email: forgotEmail }, { withCredentials: true });
      setForgotSuccess(t("login.verificationCodeSent"));
    } catch { setForgotError(t("login.serverError")); }
    finally { setForgotLoading(false); }
  };

  const handleVerifyForgotCode = async () => {
    if (!forgotCode) { setForgotError(t("login.codeRequired")); return; }
    setForgotLoading(true); setForgotError(""); setForgotSuccess("");
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/verify-email-code`, { code: forgotCode, email: forgotEmail }, { withCredentials: true });
      setForgotSuccess(t("login.emailVerified"));
      setForgotStep("resetLink");
    } catch (err) { setForgotError(err?.response?.data?.error || t("login.serverError")); }
    finally { setForgotLoading(false); }
  };

  const handleSendForgotResetLink = async () => {
    setForgotLoading(true); setForgotError(""); setForgotSuccess("");
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/forgot-password-login`, { email: forgotEmail });
      setForgotSuccess(t(`login.${res.data.message}`));
    } catch { setForgotError(t("login.serverError")); }
    finally { setForgotLoading(false); }
  };

  const getStepIcon = () => {
    if (forgotStep === "code") return <Security fontSize="small" />;
    if (forgotStep === "resetLink") return <LockReset fontSize="small" />;
    return <Key fontSize="small" />;
  };

  const inputSx = {
    backgroundColor: "rgba(255,255,255,0.95)", borderRadius: 2,
    "&:hover": { backgroundColor: "#fff", boxShadow: "0 0 0 2px rgba(102,126,234,0.3)" },
    "&.Mui-focused": { backgroundColor: "#fff", boxShadow: "0 0 0 2px rgba(102,126,234,0.5)" },
  };

  const alertSx = (rgb) => ({
    mt: 1.5, borderRadius: 2,
    backgroundColor: `rgba(${rgb},0.15)`,
    border: `1px solid rgba(${rgb},0.3)`,
    color: rgb.startsWith("244") ? "#ffcdd2" : "#c8e6c9",
    "& .MuiAlert-icon": { color: rgb.startsWith("244") ? "#ff6b6b" : "#4caf50" },
  });

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1.5fr" },
      gridTemplateAreas: {
        xs: `"form" "news"`,
        md: `"news form"`
      },
      minHeight: "100vh",
    }}>

      <Box sx={{
        background: "#07071f",
        borderRight: { md: "1px solid rgba(102,126,234,0.15)" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        px: { xs: 4, md: 6, lg: 8 },
        py: 6,
        overflowY: "auto",
        gridArea: "news",
      }}>
        {/* <RadarAnimation /> */}

        <BrandHeader />

        <Box sx={{
          width: "100%", maxWidth: 360,
          display: "flex", alignItems: "center", gap: 1.5,
        }}>
          <Typography sx={{
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(220,220,255,0.9)",
            whiteSpace: "nowrap",
            fontFamily: '"Inter", "Roboto", sans-serif',
          }}>
            {t("login.newsSectionTitle")}
          </Typography>
          <Box sx={{ flex: 1, height: "1px", background: "rgba(102,126,234,0.15)" }} />
        </Box>

        <WhatsAppBotCard />
        <DataDrillingCard />
      </Box>

      <Box sx={{
        background: "#ffffff",
        // background: "#0b0b3a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 3, md: 6, lg: 8 },
        py: 6,
        overflowY: "auto",
        gridArea: "form",
      }}>
        <Paper elevation={0} sx={{
          width: "100%",
          maxWidth: 400,
          pt: { xs: 5, sm: 6 },
          px: { xs: 3, sm: 4 },
          pb: { xs: 4, sm: 5 },
          borderRadius: 3,
          backgroundColor: "#0f0f47",
          color: "#fff",
          border: "1px solid rgba(102,126,234,0.22)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute", top: 0, left: 0, right: 0, height: "3px",
            background: "linear-gradient(90deg,#667eea 0%,#764ba2 50%,#667eea 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 3s infinite linear",
          },
          "@keyframes shimmer": {
            "0%": { backgroundPosition: "200% 0" },
            "100%": { backgroundPosition: "-200% 0" },
          },
        }}>

          <Typography variant="h5" align="center" fontWeight={700} sx={{
            mb: 3, letterSpacing: "0.5px",
            background: "linear-gradient(90deg,#fff 0%,#a0a0ff 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            {t("login.title")}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>

            <TextField
              fullWidth placeholder={t("login.usernameOrEmailPlaceholder")}
              name="loginInput" value={formData.loginInput}
              onChange={handleChange} margin="normal" required
              error={!!error.loginInput}
              helperText={error.loginInput ? t(`login.${error.loginInput}`) : ""}
              InputProps={{ sx: inputSx }}
            />

            <TextField
              fullWidth placeholder={t("login.passwordPlaceholder")}
              name="password" type={showPassword ? "text" : "password"}
              value={formData.password} onChange={handleChange}
              margin="normal" required
              error={!!error.password}
              helperText={error.password ? t(`login.${error.password}`) : ""}
              InputProps={{
                sx: inputSx,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((p) => !p)} edge="end"
                      sx={{ color: "#667eea", "&:hover": { backgroundColor: "rgba(102,126,234,0.1)" } }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox name="rememberMe" checked={formData.rememberMe} onChange={handleChange}
                  sx={{ color: "#a0a0d0", "&.Mui-checked": { color: "#667eea" } }}
                />
              }
              label={
                <Typography sx={{ color: "#d0d0f0", fontSize: "0.9rem", fontWeight: 500 }}>
                  {t("login.rememberMe")}
                </Typography>
              }
              sx={{ mt: 1, ml: 0.5 }}
            />

            {serverError && (
              <Fade in>
                <Alert severity="error" icon={<Security fontSize="small" />} sx={alertSx("244,67,54")}>
                  {t(`login.${serverError}`)}
                </Alert>
              </Fade>
            )}

            <Button
              type="submit" fullWidth variant="contained" disabled={loading}
              sx={{
                mt: 3, py: 1.5, fontWeight: 600, fontSize: "1rem", letterSpacing: "0.5px",
                background: loading
                  ? "rgba(255,255,255,0.08)"
                  : "linear-gradient(135deg,#667eea 0%,#764ba2 100%)",
                color: "#fff", borderRadius: 2,
                boxShadow: "0 4px 20px rgba(102,126,234,0.3)",
                "&:hover": { boxShadow: "0 6px 24px rgba(102,126,234,0.5)", transform: "translateY(-1px)" },
                "&:disabled": { background: "rgba(255,255,255,0.08)", boxShadow: "none", color: "rgba(255,255,255,0.4)" },
              }}
            >
              {loading
                ? <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                  {t("login.loadingButton")}
                </Box>
                : t("login.submitButton")}
            </Button>

            <Typography
              onClick={() => { setForgotStep("email"); setForgotCode(""); setForgotError(""); setForgotSuccess(""); }}
              sx={{
                mt: 2.5, cursor: "pointer", textAlign: "center",
                color: "#a0a0d0", fontSize: "0.9rem", fontWeight: 500,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
                "&:hover": { color: "#667eea" },
              }}
            >
              <Key fontSize="small" />
              {t("login.forgotPassword")}
            </Typography>
          </Box>

          <Collapse in={forgotStep !== "hidden"} timeout={350}>
            <Box sx={{
              mt: 3, p: 2.5,
              background: "rgba(20,20,75,0.8)",
              borderRadius: 2,
              border: "1px solid rgba(102,126,234,0.28)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "10px",
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {getStepIcon()}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                      {forgotStep === "email" && t("login.recoveryTitle")}
                      {forgotStep === "code" && t("login.verifyEmailTitle")}
                      {forgotStep === "resetLink" && t("login.resetPasswordTitle")}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#a0a0d0" }}>
                      {forgotStep === "email" && t("login.step1")}
                      {forgotStep === "code" && t("login.step2")}
                      {forgotStep === "resetLink" && t("login.step3")}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={resetForgotState} size="small"
                  sx={{ color: "#a0a0d0", "&:hover": { color: "#fff", backgroundColor: "rgba(102,126,234,0.2)" } }}>
                  <ArrowBack fontSize="small" />
                </IconButton>
              </Box>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

              {forgotStep === "email" && (
                <>
                  <Typography variant="body2" sx={{ color: "#d0d0f0", mb: 2, lineHeight: 1.6 }}>
                    {t("login.enterEmailForRecovery")}
                  </Typography>
                  <TextField fullWidth size="small"
                    placeholder={t("login.email")} value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    InputProps={{ sx: { ...inputSx, borderRadius: 1.5 } }} sx={{ mb: 2 }}
                  />
                </>
              )}

              {forgotStep === "code" && (
                <>
                  <Typography variant="body2" sx={{ color: "#d0d0f0", mb: 2, lineHeight: 1.6 }}>
                    {t("login.enterCodeSentToEmail")}
                  </Typography>
                  <TextField fullWidth size="small"
                    placeholder={t("login.code")} value={forgotCode}
                    onChange={(e) => setForgotCode(e.target.value)}
                    InputProps={{ sx: { ...inputSx, borderRadius: 1.5, fontFamily: "'Roboto Mono', monospace", letterSpacing: "2px" } }}
                    sx={{ mb: 1.5 }}
                  />
                  <Typography onClick={handleResendForgotCode} sx={{
                    fontSize: "0.82rem", color: "#a0a0d0", cursor: "pointer", mb: 1.5,
                    display: "inline-flex", alignItems: "center", gap: 0.5,
                    "&:hover": { color: "#667eea" },
                  }}>
                    <Send fontSize="small" />
                    {t("login.resendVerificationCode")}
                  </Typography>
                </>
              )}

              {forgotStep === "resetLink" && (
                <>
                  <Typography variant="body2" sx={{ color: "#d0d0f0", mb: 2, lineHeight: 1.6 }}>
                    {t("login.sendRecoveryLinkDescription")}
                  </Typography>
                  <Box sx={{
                    p: 2, mb: 2,
                    backgroundColor: "rgba(102,126,234,0.08)",
                    border: "1px solid rgba(102,126,234,0.22)",
                    borderRadius: 1.5,
                  }}>
                    <Typography variant="body2" sx={{ color: "#d0d0f0", textAlign: "center" }}>
                      {forgotEmail}
                    </Typography>
                  </Box>
                </>
              )}

              {forgotError && (
                <Fade in>
                  <Alert severity="error" icon={<Security fontSize="small" />} sx={{ ...alertSx("244,67,54"), mb: 1.5 }}>
                    {forgotError}
                  </Alert>
                </Fade>
              )}
              {forgotSuccess && (
                <Fade in>
                  <Alert severity="success" icon={<VerifiedUser fontSize="small" />} sx={{ ...alertSx("76,175,80"), mb: 1.5 }}>
                    {t(forgotSuccess)}
                  </Alert>
                </Fade>
              )}

              <Button fullWidth variant="contained"
                onClick={
                  forgotStep === "email" ? handleForgotEmailSubmit :
                    forgotStep === "code" ? handleVerifyForgotCode :
                      handleSendForgotResetLink
                }
                disabled={forgotLoading}
                startIcon={
                  forgotLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> :
                    forgotStep === "email" ? <Send /> :
                      forgotStep === "code" ? <VerifiedUser /> :
                        <LockReset />
                }
                sx={{
                  py: 1.25, borderRadius: 1.5, fontWeight: 600, textTransform: "none",
                  background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)",
                  color: "#fff", boxShadow: "0 4px 16px rgba(102,126,234,0.3)",
                  "&:hover": { boxShadow: "0 6px 20px rgba(102,126,234,0.45)", transform: "translateY(-1px)" },
                  "&:disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)", boxShadow: "none" },
                }}
              >
                {forgotLoading
                  ? t("login.loadingButton")
                  : forgotStep === "email" ? t("login.sendEmailButton")
                    : forgotStep === "code" ? t("login.verifyCodeButton")
                      : t("login.sendResetLinkButton")}
              </Button>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mt: 2.5, pt: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                {["email", "code", "resetLink"].map((step, i) => (
                  <Box key={step} sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: "50%",
                      backgroundColor: forgotStep === step ? "#667eea" : "rgba(255,255,255,0.18)",
                      transition: "background-color 0.3s ease",
                    }} />
                    {i < 2 && (
                      <Box sx={{
                        width: 18, height: 2, mx: 0.5, borderRadius: 1,
                        backgroundColor:
                          (forgotStep === "code" && i === 0) || forgotStep === "resetLink"
                            ? "#667eea" : "rgba(255,255,255,0.15)",
                        transition: "background-color 0.3s ease",
                      }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Collapse>

        </Paper>
      </Box>
    </Box>
  );
};

export default Login;